/**
 * EAS Print View Component
 * Browser-native print view replacing @react-pdf/renderer
 * Design: "Editorial Executive" - McKinsey/Bloomberg aesthetic
 */

import React, { useEffect, useState, useMemo } from 'react';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend } from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';
import './EASPrintView.css';
import logoImage from '../../images/logo_PTG_retina.png';
import { calculateProjectionsWithEscalation, DEFAULT_ESCALATION_RATE } from '../../services/easCalculations';

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend);

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

const formatDate = () => {
  return new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const formatCurrency = (val) => {
  if (val === null || val === undefined || isNaN(val)) return '$0';
  if (Math.abs(val) >= 1000000) return `$${(val / 1000000).toFixed(2)}M`;
  if (Math.abs(val) >= 1000) return `$${(val / 1000).toFixed(1)}K`;
  return `$${val.toFixed(2)}`;
};

const formatPercent = (val) => {
  if (val === null || val === undefined || isNaN(val)) return '0%';
  return `${(val * 100).toFixed(1)}%`;
};

const formatMonths = (val) => {
  if (val === null || val === undefined || isNaN(val)) return '0';
  return val.toFixed(1);
};

// ============================================================================
// REUSABLE COMPONENTS
// ============================================================================

function PageHeader({ title = 'Assessment Estimate' }) {
  return (
    <header className="page-header">
      <img src={logoImage} alt="Powertron Global" className="page-header-logo" />
      <div className="page-header-info">
        <h2 className="page-header-title">{title}</h2>
        <p className="page-header-date">{formatDate()}</p>
      </div>
    </header>
  );
}

function PageFooter({ pageNumber, totalPages = 10 }) {
  return (
    <footer className="page-footer">
      <span>Powertron Global</span>
      <span className="page-footer-center">Page {pageNumber} of {totalPages}</span>
      <span>sales@powertronglobal.com</span>
    </footer>
  );
}

function MetricCard({ label, value, subtext, variant = 'default', className = '' }) {
  const cardClass = `metric-card ${variant !== 'default' ? variant : ''} ${className}`.trim();
  const valueClass = `metric-value ${variant === 'positive' ? 'positive' : variant === 'negative' ? 'negative' : ''}`.trim();

  return (
    <div className={cardClass}>
      <div className="metric-label">{label}</div>
      <div className={valueClass}>{value}</div>
      {subtext && <div className="metric-subtext">{subtext}</div>}
    </div>
  );
}

function EquipmentTable({ byType }) {
  if (!byType || Object.keys(byType).length === 0) return null;

  const rows = Object.entries(byType)
    .filter(([type, data]) => data.count > 0 && type !== 'Unknown')
    .map(([type, data]) => ({
      type,
      units: data.count || 0,
      tonnage: data.total_tonnage || 0,
      cost: data.total_treatment_cost || 0,
      savings: data.total_annual_savings || 0,
      payback: data.average_payback_months || 0,
    }));

  return (
    <table className="print-table">
      <thead>
        <tr>
          <th>Type</th>
          <th className="text-center">Units</th>
          <th className="text-right">Tonnage/Unit</th>
          <th className="text-right">Cost</th>
          <th className="text-right">Annual Savings</th>
          <th className="text-right">Payback</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row, idx) => (
          <tr key={idx}>
            <td>{row.type}</td>
            <td className="text-center font-mono">{row.units}</td>
            <td className="text-right font-mono">{row.tonnage.toLocaleString()}</td>
            <td className="text-right font-mono">{formatCurrency(row.cost)}</td>
            <td className="text-right font-mono">{formatCurrency(row.savings)}</td>
            <td className="text-right font-mono">{formatMonths(row.payback)} mo</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ValueBreakdownTable({ valueBreakdown }) {
  if (!valueBreakdown) return null;

  const components = [
    { key: 'energy_savings', label: 'Energy Savings', footnote: '¹' },
    { key: 'avoided_degradation', label: 'Avoided Degradation', footnote: '²' },
    { key: 'avoided_demand', label: 'Avoided Demand Charges', footnote: '³' },
    { key: 'avoided_runtime', label: 'Avoided Runtime Costs', footnote: '³' },
    { key: 'life_extension', label: 'Life Extension Value', footnote: '³' },
    { key: 'mission_critical_protection', label: 'Mission-Critical Protection', footnote: '³' },
  ];

  // Filter to only show components with values > 0
  const activeComponents = components.filter(c => (valueBreakdown[c.key] || 0) > 0);
  if (activeComponents.length === 0) return null;

  const total = activeComponents.reduce((sum, c) => sum + (valueBreakdown[c.key] || 0), 0);

  return (
    <>
      <table className="print-table value-breakdown-table">
        <thead>
          <tr>
            <th>Value Component</th>
            <th className="text-right">Annual Value</th>
            <th className="text-right">% of Total</th>
          </tr>
        </thead>
        <tbody>
          {activeComponents.map((c) => {
            const value = valueBreakdown[c.key] || 0;
            const pct = total > 0 ? (value / total * 100) : 0;
            return (
              <tr key={c.key}>
                <td>{c.label}<sup style={{ color: '#888', fontSize: '0.7em' }}>{c.footnote}</sup></td>
                <td className="text-right font-mono">{formatCurrency(value)}</td>
                <td className="text-right font-mono">{pct.toFixed(1)}%</td>
              </tr>
            );
          })}
          <tr className="total-row">
            <td><strong>Total Annual Value</strong></td>
            <td className="text-right font-mono"><strong>{formatCurrency(total)}</strong></td>
            <td className="text-right font-mono"><strong>100%</strong></td>
          </tr>
        </tbody>
      </table>
      <div style={{ marginTop: '8px', fontSize: '0.65rem', color: '#888', lineHeight: 1.5 }}>
        <span>¹ PE-Certified: Based on 129 IPMVP-compliant field measurements</span><br/>
        <span>² ASHRAE: Based on ASHRAE 1998 Handbook Ch 2.9 + 82 corpus age measurements</span><br/>
        <span>³ Industry Estimate: Based on engineering models and industry standards; actual results may vary</span>
      </div>
    </>
  );
}

// ============================================================================
// PAGE 1: EXECUTIVE SUMMARY
// ============================================================================

function Page1Summary({ roiResults, cfoMetrics, paymentMetrics }) {
  const summary = roiResults?.portfolio?.summary || {};
  const valueBreakdown = roiResults?.portfolio?.value_breakdown || {};
  const valueComposition = roiResults?.portfolio?.value_composition || {};
  const upfront = paymentMetrics?.upfront || {};
  const threeYear = paymentMetrics?.threeYear || {};

  const hasComprehensiveValue = summary.total_annual_value && summary.total_annual_savings &&
    summary.total_annual_value !== summary.total_annual_savings;
  const valueMultiplier = hasComprehensiveValue
    ? (summary.total_annual_value / summary.total_annual_savings).toFixed(1)
    : null;

  const energySavingsPct = valueComposition?.energy_savings_pct || 46;
  const avoidedCostsPct = 100 - energySavingsPct;

  return (
    <div className="print-page">
      <PageHeader />

      <h1 className="print-title">Equipment Assessment Estimate</h1>
      <p className="print-subtitle">PermaFrost NMR Treatment Analysis</p>

      {/* CFO Financial Metrics */}
      <h2 className="section-title">CFO Financial Metrics</h2>
      <div className="metrics-row">
        <MetricCard
          label="EBITDA Impact"
          value={formatCurrency(cfoMetrics?.tier1_ebitda?.contribution)}
          subtext="Annual contribution"
        />
        <MetricCard
          label="Revenue Equivalent"
          value={formatCurrency(cfoMetrics?.tier2_revenue?.equivalentRevenue)}
          subtext="At 5% margin"
          variant="accent"
        />
        <MetricCard
          label="Enterprise Value"
          value={formatCurrency(
            cfoMetrics?.tier3_valuation?.enterpriseValueIncrease ||
            cfoMetrics?.tier3_valuation?.marketCapIncrease ||
            cfoMetrics?.tier3_valuation?.valueIncrease
          )}
          subtext="@ 8x EBITDA"
        />
        <MetricCard
          label="5-Year NPV"
          value={formatCurrency(cfoMetrics?.tier4_capital?.npv)}
          subtext="@ 8% discount"
          variant="positive"
        />
      </div>

      {/* 6-Component Value Breakdown */}
      {hasComprehensiveValue && (
        <>
          <h2 className="section-title">6-Component Value Breakdown</h2>
          <ValueBreakdownTable valueBreakdown={valueBreakdown} />
          <div className="info-box accent" style={{ marginTop: '12px' }}>
            <div className="info-box-text">
              <strong>Value Insight:</strong> The 6-component model captures <strong>{valueMultiplier}×</strong> more value than energy-only analysis.
              True Payback: <strong>{formatMonths(summary.weighted_average_payback_months)} mo</strong> vs Energy-Only: <strong>{formatMonths(summary.energy_only_payback_months || (summary.total_treatment_cost / summary.total_annual_savings * 12))} mo</strong>
            </div>
          </div>
        </>
      )}

      {/* Payment Options Comparison */}
      <h2 className="section-title">Payment Options</h2>
      <div className="comparison-grid">
        <div className="comparison-column">
          <div className="comparison-header">Upfront Payment</div>
          <div className="metric-inline">
            <span className="metric-inline-label">Total Cost</span>
            <span className="metric-inline-value">{formatCurrency(upfront.totalCost)}</span>
          </div>
          <div className="metric-inline">
            <span className="metric-inline-label">Payback Period</span>
            <span className="metric-inline-value">{formatMonths(upfront.paybackMonths)} months</span>
          </div>
          <div className="metric-inline">
            <span className="metric-inline-label">Year 1 Net</span>
            <span className={`metric-inline-value ${upfront.year1NetBenefit >= 0 ? 'text-positive' : 'text-negative'}`}>
              {formatCurrency(upfront.year1NetBenefit)}
            </span>
          </div>
          <div className="metric-inline">
            <span className="metric-inline-label">5-Year Net Profit</span>
            <span className="metric-inline-value text-positive">{formatCurrency(upfront.fiveYearNetProfit)}</span>
          </div>
        </div>

        <div className="comparison-column highlight">
          <div className="comparison-header">3-Year Deferred</div>
          <div className="metric-inline">
            <span className="metric-inline-label">Monthly Payment</span>
            <span className="metric-inline-value">{formatCurrency(threeYear.monthlyPayment)}/mo</span>
          </div>
          <div className="metric-inline">
            <span className="metric-inline-label">Monthly Cash Flow</span>
            <span className="metric-inline-value text-positive">+{formatCurrency(threeYear.monthlyCashFlow)}/mo</span>
          </div>
          <div className="metric-inline">
            <span className="metric-inline-label">Year 1 Net</span>
            <span className="metric-inline-value text-positive">{formatCurrency(threeYear.year1NetBenefit)}</span>
          </div>
          <div className="metric-inline">
            <span className="metric-inline-label">5-Year Net Profit</span>
            <span className="metric-inline-value text-positive">{formatCurrency(threeYear.fiveYearNetProfit)}</span>
          </div>
        </div>
      </div>
      <p className="credit-disclaimer">3-year deferred payment available to qualified buyers, subject to credit approval.</p>

      {/* Equipment Summary */}
      <h2 className="section-title">Equipment Summary</h2>
      <div className="equipment-summary-bar">
        <div className="equipment-summary-item">
          <div className="equipment-summary-value">{summary.total_equipment || 0}</div>
          <div className="equipment-summary-label">Units</div>
        </div>
        <div className="equipment-summary-item">
          <div className="equipment-summary-value">{(summary.total_tonnage || 0).toLocaleString()}</div>
          <div className="equipment-summary-label">Tons</div>
        </div>
        <div className="equipment-summary-item">
          <div className="equipment-summary-value">{formatMonths(summary.weighted_average_payback_months)} mo</div>
          <div className="equipment-summary-label">Payback</div>
        </div>
        <div className="equipment-summary-item">
          <div className="equipment-summary-value">{(summary.five_year_roi || 0).toFixed(0)}%</div>
          <div className="equipment-summary-label">5-Yr ROI</div>
        </div>
      </div>

      <EquipmentTable byType={roiResults?.byType} />

      <PageFooter pageNumber={1} />
    </div>
  );
}

// ============================================================================
// PAGE 2: UPFRONT PAYMENT ANALYSIS
// ============================================================================

function Page2Upfront({ roiResults, cfoMetrics, paymentMetrics }) {
  const summary = roiResults?.portfolio?.summary || {};
  const upfront = paymentMetrics?.upfront || {};
  const tier1 = cfoMetrics?.tier1_ebitda || {};
  const tier2 = cfoMetrics?.tier2_revenue || {};
  const tier3 = cfoMetrics?.tier3_valuation || {};
  const tier4 = cfoMetrics?.tier4_capital || {};

  // Waterfall chart data
  const waterfallData = {
    labels: ['Treatment Cost', 'Year 1', 'Year 2', 'Year 3', 'Year 4', 'Year 5', 'Total'],
    datasets: [{
      label: 'Cumulative Value',
      data: [
        -(summary.total_treatment_cost || 0),
        (summary.total_annual_savings || 0) - (summary.total_treatment_cost || 0),
        (summary.total_annual_savings || 0) * 2 - (summary.total_treatment_cost || 0),
        (summary.total_annual_savings || 0) * 3 - (summary.total_treatment_cost || 0),
        (summary.total_annual_savings || 0) * 4 - (summary.total_treatment_cost || 0),
        (summary.total_annual_savings || 0) * 5 - (summary.total_treatment_cost || 0),
        (summary.total_annual_savings || 0) * 5 - (summary.total_treatment_cost || 0),
      ],
      backgroundColor: (ctx) => {
        const value = ctx.raw;
        return value >= 0 ? '#166534' : '#991B1B';
      },
      borderRadius: 4,
    }],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      title: { display: false },
      datalabels: { display: false },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: (value) => formatCurrency(value),
          font: { family: 'JetBrains Mono', size: 10 },
          maxTicksLimit: 6,
        },
        grid: { color: '#E2E8F0' },
      },
      x: {
        ticks: { font: { family: 'Source Sans 3', size: 10 } },
        grid: { display: false },
      },
    },
  };

  return (
    <div className="print-page">
      <PageHeader title="Upfront Payment Analysis" />

      <h1 className="print-title">Upfront Payment Option</h1>
      <p className="print-subtitle">Full investment upfront with immediate ownership</p>

      {/* Investment Overview */}
      <h2 className="section-title">Investment Overview</h2>
      <div className="metrics-row">
        <MetricCard label="Total Investment" value={formatCurrency(upfront.totalCost)} />
        <MetricCard label="Annual Savings" value={formatCurrency(summary.total_annual_savings)} variant="positive" />
        <MetricCard label="Payback Period" value={`${formatMonths(upfront.paybackMonths)} mo`} />
        <MetricCard label="5-Year Net Profit" value={formatCurrency(upfront.fiveYearNetProfit)} variant="positive" />
      </div>

      {/* Strategic Value Creation */}
      <h2 className="section-title">Strategic Value Creation</h2>
      <div className="metrics-row metrics-row-3">
        <MetricCard label="EBITDA Contribution" value={formatCurrency(tier1.contribution)} subtext="Direct bottom-line impact" />
        <MetricCard label="Revenue Equivalent" value={formatCurrency(tier2.equivalentRevenue)} subtext="At 5% profit margin" variant="accent" />
        <MetricCard
          label="Enterprise Value"
          value={formatCurrency(tier3.enterpriseValueIncrease || tier3.marketCapIncrease || tier3.valueIncrease)}
          subtext="@ 8x EBITDA multiple"
        />
      </div>

      {/* Capital Efficiency */}
      <h2 className="section-title">Capital Efficiency</h2>
      <div className="metrics-row">
        <MetricCard label="Net Present Value" value={formatCurrency(tier4.npv)} subtext="@ 8% discount rate" variant="positive" />
        <MetricCard label="IRR" value={formatPercent(tier4.irr / 100)} subtext="vs 8% cost of capital" />
        <MetricCard label="ROIC" value={formatPercent(tier4.roic / 100)} subtext="Return on invested capital" />
        <MetricCard label="Payback" value={`${formatMonths(tier4.paybackMonths)} mo`} subtext="Cash breakeven" />
      </div>

      {/* 5-Year Value Waterfall */}
      <h2 className="section-title">5-Year Value Trajectory</h2>
      <div className="chart-container" style={{ height: '220px' }}>
        <Bar data={waterfallData} options={chartOptions} />
      </div>
      <p className="chart-caption">
        Cumulative value over 5 years. Treatment cost is recovered within the first year, with compounding returns thereafter.
      </p>

      <PageFooter pageNumber={2} />
    </div>
  );
}

// ============================================================================
// PAGE 3: ENERGY & ESG IMPACT
// ============================================================================

function Page3EnergyESG({ roiResults, cfoMetrics }) {
  const summary = roiResults?.portfolio?.summary || {};
  const tier5 = cfoMetrics?.tier5_esg || {};

  // Before/After chart data
  const avgAge = 8; // Approximation
  const yearsWasted = avgAge;
  const yearsProjected = 10;

  const beforeAfterData = {
    labels: [...Array(yearsWasted + yearsProjected + 1).keys()].map(i =>
      i <= yearsWasted ? `Y-${yearsWasted - i}` : `Y+${i - yearsWasted}`
    ),
    datasets: [
      {
        label: 'Energy Wasted (Before)',
        data: [...Array(yearsWasted + 1).keys()].map(() => (summary.total_annual_savings || 0) * 0.8),
        backgroundColor: '#991B1B',
        stack: 'stack1',
      },
      {
        label: 'Energy Saved (After)',
        data: [
          ...Array(yearsWasted + 1).fill(null),
          ...Array(yearsProjected).fill(summary.total_annual_savings || 0),
        ],
        backgroundColor: '#166534',
        stack: 'stack1',
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: { font: { family: 'Source Sans 3', size: 10 } },
      },
      datalabels: { display: false },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: (value) => formatCurrency(value),
          font: { family: 'JetBrains Mono', size: 10 },
          maxTicksLimit: 6,
        },
        grid: { color: '#E2E8F0' },
      },
      x: {
        ticks: { font: { family: 'Source Sans 3', size: 9 } },
        grid: { display: false },
      },
    },
  };

  return (
    <div className="print-page">
      <PageHeader title="Energy & ESG Impact" />

      <h1 className="print-title">Energy & ESG Impact</h1>
      <p className="print-subtitle">Environmental and sustainability metrics</p>

      {/* ESG Metrics */}
      <h2 className="section-title">ESG & Sustainability</h2>
      <div className="metrics-row">
        <MetricCard
          label="CO₂ Avoided"
          value={`${(tier5.co2AvoidedTonnes || 0).toFixed(1)} t/yr`}
          subtext="Annual reduction"
          variant="positive"
        />
        <MetricCard
          label="Vehicle Equivalent"
          value={`${tier5.vehicleEquivalent || 0} cars`}
          subtext="Removed from road"
        />
        <MetricCard
          label="Trees Equivalent"
          value={`${Math.round((tier5.co2AvoidedTonnes || 0) * 45.4)}`}
          subtext="trees planted equiv"
          variant="positive"
        />
        <MetricCard
          label="Energy Saved"
          value={`${((summary.total_kwh_saved || 0) / 1000).toFixed(0)}K kWh`}
          subtext="Annual savings"
        />
      </div>

      <div className="info-box">
        <div className="info-box-title">ESG Reporting Value</div>
        <div className="info-box-text">
          CO₂ reductions directly support Scope 2 emissions reporting. These metrics can be included in sustainability
          disclosures and ESG frameworks (GRI, SASB, CDP). Vehicle equivalents help communicate impact to stakeholders
          in relatable terms.
        </div>
      </div>

      {/* Before/After Chart */}
      <h2 className="section-title">Energy Impact: Before vs After</h2>
      <div className="chart-container" style={{ height: '200px' }}>
        <Bar data={beforeAfterData} options={chartOptions} />
      </div>
      <p className="chart-caption">
        Red bars show energy wasted before treatment. Green bars show projected savings after treatment.
        Each year of delay adds to cumulative waste.
      </p>

      {/* Equipment Breakdown */}
      <h2 className="section-title">Equipment Breakdown</h2>
      <EquipmentTable byType={roiResults?.byType} />

      <div className="methodology-note">
        <strong>Methodology:</strong> CO₂ calculations use EPA average of 0.42 kg CO₂/kWh. Vehicle equivalents
        based on average passenger vehicle emitting 4.6 metric tons CO₂/year. Equipment-specific efficiency
        improvements validated: Chillers 21.1%, RTUs 22.3%, CRACs 26.1%, Split Systems 24.3%.
      </div>

      <PageFooter pageNumber={3} />
    </div>
  );
}

// ============================================================================
// PAGE 4: YEAR-BY-YEAR PROJECTIONS (NEW)
// ============================================================================

function Page4Projections({ roiResults, paymentMetrics }) {
  const summary = roiResults?.portfolio?.summary || {};
  const escalationRate = DEFAULT_ESCALATION_RATE; // 3% default

  // Generate 10-year projection data
  const projectionData = useMemo(() => {
    if (!summary.total_annual_value) return null;
    return calculateProjectionsWithEscalation(
      summary.total_annual_value,
      10,
      escalationRate,
      summary.total_treatment_cost
    );
  }, [summary.total_annual_value, summary.total_treatment_cost]);

  if (!projectionData) return null;

  const { yearlyProjections, summary: projSummary } = projectionData;

  // Calculate net position for 3-year payment (1/3 paid each year for years 1-3)
  const getThreeYearNetPosition = (year, cumulative) => {
    const paidAmount = Math.min(year, 3) * (summary.total_treatment_cost / 3);
    return cumulative - paidAmount;
  };

  return (
    <div className="print-page">
      <PageHeader title="Year-by-Year Projections" />

      <h1 className="print-title">10-Year Value Projections</h1>
      <p className="print-subtitle">With {(escalationRate * 100).toFixed(0)}% annual electricity price escalation</p>

      {/* Summary Row */}
      <div className="metrics-row">
        <MetricCard
          label="10-Year Total (with escalation)"
          value={formatCurrency(projSummary.totalWithEscalation)}
          variant="positive"
        />
        <MetricCard
          label="10-Year Total (flat)"
          value={formatCurrency(projSummary.totalWithoutEscalation)}
        />
        <MetricCard
          label="Escalation Benefit"
          value={`+${formatCurrency(projSummary.escalationBenefit)}`}
          subtext={`+${projSummary.escalationBenefitPct.toFixed(1)}%`}
          variant="accent"
        />
      </div>

      {/* Side-by-Side Projection Tables */}
      <div className="side-by-side-grid">
        {/* Upfront Payment Table */}
        <div>
          <h2 className="section-title" style={{ color: '#1B4332' }}>Upfront Payment</h2>
          <table className="print-table projection-table">
            <thead>
              <tr>
                <th>Year</th>
                <th className="text-right">Annual</th>
                <th className="text-right">Cumulative</th>
                <th className="text-right">Net Position</th>
              </tr>
            </thead>
            <tbody>
              {yearlyProjections.map((row) => (
                <tr key={row.year} className={row.netPosition >= 0 ? 'row-positive' : ''}>
                  <td>Year {row.year}</td>
                  <td className="text-right font-mono">{formatCurrency(row.annualValue)}</td>
                  <td className="text-right font-mono">{formatCurrency(row.cumulative)}</td>
                  <td className={`text-right font-mono ${row.netPosition >= 0 ? 'positive' : 'negative'}`}>
                    {row.netPosition >= 0 ? '+' : ''}{formatCurrency(row.netPosition)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* 3-Year Payment Table */}
        <div>
          <h2 className="section-title" style={{ color: '#0D9488' }}>3-Year Payment</h2>
          <p className="credit-disclaimer" style={{ marginTop: 0 }}>Available to qualified buyers, subject to credit approval.</p>
          <table className="print-table projection-table">
            <thead>
              <tr>
                <th>Year</th>
                <th className="text-right">Annual</th>
                <th className="text-right">Payment</th>
                <th className="text-right">Net Position</th>
              </tr>
            </thead>
            <tbody>
              {yearlyProjections.map((row) => {
                const payment = row.year <= 3 ? summary.total_treatment_cost / 3 : 0;
                const netPosition = getThreeYearNetPosition(row.year, row.cumulative);
                return (
                  <tr key={row.year} className={netPosition >= 0 ? 'row-positive' : ''}>
                    <td>Year {row.year}</td>
                    <td className="text-right font-mono">{formatCurrency(row.annualValue)}</td>
                    <td className="text-right font-mono">{row.year <= 3 ? formatCurrency(payment) : '—'}</td>
                    <td className={`text-right font-mono ${netPosition >= 0 ? 'positive' : 'negative'}`}>
                      {netPosition >= 0 ? '+' : ''}{formatCurrency(netPosition)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="info-box" style={{ marginTop: '16px' }}>
        <div className="info-box-text">
          <strong>Escalation Impact:</strong> Electricity prices historically increase 2-4% annually.
          At {(escalationRate * 100).toFixed(0)}% escalation, Year 10 savings are {((Math.pow(1 + escalationRate, 9) - 1) * 100).toFixed(0)}% higher than Year 1,
          making treatment more valuable over time.
        </div>
      </div>

      <PageFooter pageNumber={4} />
    </div>
  );
}

// ============================================================================
// PAGE 5: 5-YEAR VALUE CHARTS (NEW)
// ============================================================================

function Page5ValueCharts({ roiResults, paymentMetrics }) {
  const summary = roiResults?.portfolio?.summary || {};
  const upfront = paymentMetrics?.upfront || {};
  const threeYear = paymentMetrics?.threeYear || {};

  // Build chart data for upfront
  const upfrontChartData = {
    labels: ['Year 1', 'Year 2', 'Year 3', 'Year 4', 'Year 5'],
    datasets: [{
      label: 'Cumulative Value',
      data: [1, 2, 3, 4, 5].map(y => (summary.total_annual_value || 0) * y),
      backgroundColor: '#166534',
      borderRadius: 4,
    }, {
      label: 'Investment',
      data: [1, 2, 3, 4, 5].map(() => summary.total_treatment_cost || 0),
      backgroundColor: '#E2E8F0',
      borderRadius: 4,
    }],
  };

  // Build chart data for 3-year
  const threeYearChartData = {
    labels: ['Year 1', 'Year 2', 'Year 3', 'Year 4', 'Year 5'],
    datasets: [{
      label: 'Cumulative Value',
      data: [1, 2, 3, 4, 5].map(y => (summary.total_annual_value || 0) * y),
      backgroundColor: '#0D9488',
      borderRadius: 4,
    }, {
      label: 'Cumulative Payments',
      data: [1, 2, 3, 4, 5].map(y => Math.min(y, 3) * (threeYear.annualPayment || 0)),
      backgroundColor: '#E2E8F0',
      borderRadius: 4,
    }],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: { font: { family: 'Source Sans 3', size: 9 } },
      },
      datalabels: { display: false },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: (value) => formatCurrency(value),
          font: { family: 'JetBrains Mono', size: 9 },
          maxTicksLimit: 6,
        },
        grid: { color: '#E2E8F0' },
      },
      x: {
        ticks: { font: { family: 'Source Sans 3', size: 9 } },
        grid: { display: false },
      },
    },
  };

  return (
    <div className="print-page">
      <PageHeader title="5-Year Value Comparison" />

      <h1 className="print-title">5-Year Cumulative Value</h1>
      <p className="print-subtitle">Comparing payment options over time</p>

      {/* Summary metrics */}
      <div className="metrics-row">
        <MetricCard label="5-Year Value" value={formatCurrency((summary.total_annual_value || 0) * 5)} variant="positive" />
        <MetricCard label="Upfront Net Profit" value={formatCurrency(upfront.fiveYearNetProfit)} />
        <MetricCard label="3-Year Net Profit" value={formatCurrency(threeYear.fiveYearNetProfit)} />
        <MetricCard label="Same Outcome" value="✓" subtext="Both options equal" variant="accent" />
      </div>

      {/* Side-by-Side Charts */}
      <div className="side-by-side-grid" style={{ marginTop: '20px' }}>
        {/* Upfront Chart */}
        <div>
          <h2 className="section-title" style={{ color: '#1B4332' }}>Upfront Payment</h2>
          <div className="chart-container" style={{ height: '200px' }}>
            <Bar data={upfrontChartData} options={chartOptions} />
          </div>
          <p className="chart-caption">
            Full investment in Year 0. Payback in {formatMonths(upfront.paybackMonths)} months.
          </p>
        </div>

        {/* 3-Year Chart */}
        <div>
          <h2 className="section-title" style={{ color: '#0D9488' }}>3-Year Payment</h2>
          <p className="credit-disclaimer" style={{ marginTop: 0 }}>Available to qualified buyers, subject to credit approval.</p>
          <div className="chart-container" style={{ height: '200px' }}>
            <Bar data={threeYearChartData} options={chartOptions} />
          </div>
          <p className="chart-caption">
            Spread over 36 months. Cash-positive from Day 1.
          </p>
        </div>
      </div>

      {/* Key Insight */}
      <div className="info-box accent" style={{ marginTop: '20px' }}>
        <div className="info-box-title">Key Insight: Same 5-Year Outcome</div>
        <div className="info-box-text">
          Both payment options deliver <strong>{formatCurrency(upfront.fiveYearNetProfit)}</strong> net profit over 5 years.
          The difference is cash flow timing—upfront requires full investment immediately,
          while 3-year spreads payments and maintains positive cash flow from Day 1.
        </div>
      </div>

      <PageFooter pageNumber={5} />
    </div>
  );
}

// ============================================================================
// PAGE 6: DEFERRED PAYMENT ANALYSIS (was Page 4)
// ============================================================================

function Page6Deferred({ roiResults, paymentMetrics, cfoAnalysis }) {
  const summary = roiResults?.portfolio?.summary || {};
  const threeYear = paymentMetrics?.threeYear || {};
  const npv = cfoAnalysis?.npv || {};

  return (
    <div className="print-page">
      <PageHeader title="3-Year Payment Analysis" />

      <h1 className="print-title">3-Year Deferred Payment Option</h1>
      <p className="print-subtitle">Spread investment over 36 months with immediate positive cash flow</p>
      <p className="credit-disclaimer">Available to qualified buyers, subject to credit approval.</p>

      {/* Payment Schedule */}
      <h2 className="section-title">Payment Schedule</h2>
      <div className="metrics-row">
        <MetricCard label="Monthly Payment" value={formatCurrency(threeYear.monthlyPayment)} subtext="For 36 months" />
        <MetricCard label="Annual Payment" value={formatCurrency(threeYear.annualPayment)} subtext="Years 1-3" />
        <MetricCard label="Total Cost" value={formatCurrency(threeYear.totalCost)} subtext="No interest" />
        <MetricCard
          label="Monthly Cash Flow"
          value={`+${formatCurrency(threeYear.monthlyCashFlow)}`}
          subtext="Positive from Day 1"
          variant="positive"
        />
      </div>

      {/* Annual Cash Flow Table */}
      <h2 className="section-title">Annual Cash Flow Analysis</h2>
      <table className="print-table">
        <thead>
          <tr>
            <th>Year</th>
            <th className="text-right">Annual Savings</th>
            <th className="text-right">Annual Payment</th>
            <th className="text-right">Net Benefit</th>
          </tr>
        </thead>
        <tbody>
          {[1, 2, 3, 4, 5].map((year) => {
            const savings = summary.total_annual_savings || 0;
            const payment = year <= 3 ? threeYear.annualPayment : 0;
            const net = year <= 3 ? threeYear[`year${year}NetBenefit`] : savings;
            return (
              <tr key={year}>
                <td>Year {year}</td>
                <td className="text-right font-mono">{formatCurrency(savings)}</td>
                <td className="text-right font-mono">{formatCurrency(payment)}</td>
                <td className={`text-right font-mono ${net >= 0 ? 'positive' : 'negative'}`}>
                  {formatCurrency(net)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* NPV Advantage */}
      <div className="info-box">
        <div className="info-box-title">Time Value of Money Advantage</div>
        <div className="info-box-text">
          By deferring payment over 3 years, you benefit from the time value of money.
          At an {(npv.discountRatePct || 8).toFixed(0)}% discount rate, the NPV of the 3-year payment stream
          is <strong>{formatCurrency(npv.threeYear)}</strong> vs <strong>{formatCurrency(npv.upfront)}</strong> upfront—an
          advantage of <strong>{formatCurrency(npv.advantage)}</strong> ({(npv.advantagePct || 0).toFixed(1)}%).
        </div>
      </div>

      {/* CFO Decision Factors */}
      <h2 className="section-title">CFO Decision Factors</h2>
      <table className="print-table">
        <thead>
          <tr>
            <th>Factor</th>
            <th>Upfront</th>
            <th>3-Year Deferred</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Day 1 Cash Outlay</td>
            <td className="font-mono">{formatCurrency(summary.total_treatment_cost)}</td>
            <td className="font-mono positive">$0</td>
          </tr>
          <tr>
            <td>Day 1 Cash Flow</td>
            <td className="negative">Negative</td>
            <td className="positive">Positive</td>
          </tr>
          <tr>
            <td>Budget Category</td>
            <td>Often CapEx</td>
            <td className="positive">Often OpEx</td>
          </tr>
          <tr>
            <td>Tax Treatment</td>
            <td>May qualify for incentives</td>
            <td className="positive">May expense immediately</td>
          </tr>
          <tr>
            <td>Approval Level</td>
            <td>Higher (capital)</td>
            <td className="positive">Lower (operating)</td>
          </tr>
          <tr>
            <td>Effective Payback</td>
            <td className="font-mono">{formatMonths(summary.weighted_average_payback_months)} mo</td>
            <td className="positive font-mono">None - always profitable</td>
          </tr>
        </tbody>
      </table>
      <p className="credit-disclaimer">3-year deferred payment available to qualified buyers, subject to credit approval.</p>

      <PageFooter pageNumber={6} />
    </div>
  );
}

// ============================================================================
// PAGE 7: CFO RECOMMENDATION (NEW)
// ============================================================================

function Page7CFORecommendation({ roiResults, cfoMetrics, companyConfig }) {
  const summary = roiResults?.portfolio?.summary || {};
  const tier4 = cfoMetrics?.tier4_capital || {};

  // Calculate analysis values
  const totalEquipment = summary.count || 0;
  const capex = summary.total_treatment_cost || 0;
  const energyOnlySavings = summary.total_annual_savings || 0;
  const totalAnnualValue = summary.total_annual_value || energyOnlySavings;
  const discountRate = companyConfig?.discountRate || 8;

  // IRR calculation (simplified)
  const irrLow = capex > 0 ? ((energyOnlySavings / capex) * 100) : 0;
  const irrHigh = capex > 0 ? ((totalAnnualValue / capex) * 100) : 0;

  // Break-even threshold (at what % of projected savings do we still break even)
  const breakEvenThresholdPct = capex > 0 && totalAnnualValue > 0
    ? Math.round((capex / (totalAnnualValue * 5)) * 100)
    : 20;

  // True cash breakeven months
  const trueCashBreakevenMonths = totalAnnualValue > 0
    ? (capex / totalAnnualValue) * 12
    : 0;

  return (
    <div className="print-page">
      <PageHeader title="CFO Recommendation" />

      <h1 className="page-title">CFO Recommendation</h1>
      <p className="page-subtitle">Investment decision framework and approval guidance</p>

      {/* Approval Badge */}
      <div className="approval-badge">
        <span className="approval-icon">✓</span>
        <span className="approval-text">Approval Recommended — Decision Grade</span>
      </div>

      {/* Decision Basis */}
      <div className="decision-basis-box">
        <div className="decision-basis-header">
          <span className="verified-icon">✓</span>
          <span className="decision-basis-title">Decision Basis</span>
        </div>
        <p className="decision-basis-text">
          This investment decision is based on documented third-party field evidence and contractual
          performance guarantees. The technology has been validated across 116 Professional Engineer–certified
          measurements and is procured under enforceable performance terms.
        </p>
        <p className="decision-basis-note">
          Additional local testing is not required for approval, as performance verification occurs
          at the portfolio level through observable utility cost reductions.
        </p>
      </div>

      {/* Investment Decision */}
      <h2 className="section-title">Investment Decision</h2>
      <p className="section-intro">
        This analysis validates unit economics for a repeatable operating policy, not a single-asset capital project.
      </p>

      <div className="two-column-grid">
        {/* Decision Rationale */}
        <div className="rationale-section">
          <h3 className="subsection-title">Decision-Grade Rationale</h3>
          <ul className="rationale-list">
            <li><span className="check">✓</span> IRR {irrLow.toFixed(0)}-{irrHigh.toFixed(0)}% exceeds {discountRate}% WACC by {Math.round(irrLow / discountRate)}×</li>
            <li><span className="check">✓</span> True cash breakeven in {trueCashBreakevenMonths.toFixed(1)} months using verified energy savings only</li>
            <li><span className="check">✓</span> Validated: Returns positive even at {breakEvenThresholdPct}% of documented averages</li>
            <li><span className="check">✓</span> {trueCashBreakevenMonths.toFixed(0)}-month capital-at-risk window (upfront)</li>
            <li><span className="check">✓</span> Low execution risk: No operational disruption, reversible, proven technology</li>
          </ul>
        </div>

        {/* Strategic Score */}
        <div className="score-card">
          <div className="score-label">Strategic Fit Score</div>
          <div className="score-value">5<span className="score-max">/5</span></div>
          <div className="score-breakdown">
            <div className="score-item"><span>Margin Improvement</span><span>5/5</span></div>
            <div className="score-item"><span>Operational Resilience</span><span>5/5</span></div>
            <div className="score-item"><span>Scalability</span><span>5/5</span></div>
            <div className="score-item"><span>Execution Simplicity</span><span>5/5</span></div>
          </div>
        </div>
      </div>

      {/* Capital Opportunity Cost */}
      <h2 className="section-title">Capital Opportunity Cost</h2>
      <p className="section-intro">Comparing this investment to alternative uses of capital:</p>
      <table className="print-table opportunity-table">
        <thead>
          <tr>
            <th>Investment Option</th>
            <th className="text-center">Expected Return</th>
            <th className="text-center">Risk</th>
            <th className="text-center">Cash Breakeven</th>
          </tr>
        </thead>
        <tbody>
          <tr className="highlight-row">
            <td>★ PermaFrost NMR Treatment</td>
            <td className="text-center font-mono">{irrLow.toFixed(0)}-{irrHigh.toFixed(0)}%</td>
            <td className="text-center"><span className="risk-badge low">Very Low</span></td>
            <td className="text-center font-mono">{trueCashBreakevenMonths.toFixed(0)} mo</td>
          </tr>
          <tr>
            <td>S&P 500 Index</td>
            <td className="text-center font-mono">8-10%</td>
            <td className="text-center"><span className="risk-badge medium">Moderate</span></td>
            <td className="text-center font-mono">~10 years</td>
          </tr>
          <tr>
            <td>Corporate Bonds</td>
            <td className="text-center font-mono">4-6%</td>
            <td className="text-center"><span className="risk-badge low">Low</span></td>
            <td className="text-center font-mono">~15-20 years</td>
          </tr>
          <tr>
            <td>Real Estate</td>
            <td className="text-center font-mono">6-8%</td>
            <td className="text-center"><span className="risk-badge medium">Moderate</span></td>
            <td className="text-center font-mono">~12-15 years</td>
          </tr>
          <tr>
            <td>Cash/Money Market</td>
            <td className="text-center font-mono">3-5%</td>
            <td className="text-center"><span className="risk-badge low">Very Low</span></td>
            <td className="text-center font-mono">~20+ years</td>
          </tr>
        </tbody>
      </table>

      {/* M&V Protocol */}
      <h2 className="section-title">M&V Protocol Specification</h2>
      <div className="mv-protocol-grid">
        <div className="mv-item">
          <div className="mv-label">Metric</div>
          <div className="mv-value">kW/ton</div>
          <div className="mv-note">at matched load</div>
        </div>
        <div className="mv-item">
          <div className="mv-label">Baseline</div>
          <div className="mv-value">Not required</div>
          <div className="mv-note">116 PE-certified studies</div>
        </div>
        <div className="mv-item">
          <div className="mv-label">Verification</div>
          <div className="mv-value">1-2 years</div>
          <div className="mv-note">post-treatment</div>
        </div>
        <div className="mv-item">
          <div className="mv-label">Pass Threshold</div>
          <div className="mv-value">Near design spec</div>
          <div className="mv-note">recovery varies by age/runtime</div>
        </div>
      </div>

      {/* Approval Conditions */}
      <h2 className="section-title">Approval Conditions</h2>
      <div className="conditions-grid">
        <div className="condition-item">
          <div className="condition-label">Performance Guarantee</div>
          <div className="condition-value">Restore to design efficiency — contractually guaranteed</div>
        </div>
        <div className="condition-item">
          <div className="condition-label">Payment Structure</div>
          <div className="condition-value">3-year deferred recommended. Upfront only if tax incentives improve after-tax IRR.</div>
        </div>
        <div className="condition-item">
          <div className="condition-label">Equipment Lifecycle</div>
          <div className="condition-value">Covered by product guarantee — older systems gain life extension value</div>
        </div>
      </div>

      {/* Post-Approval Governance */}
      <h2 className="section-title">Post-Approval Governance</h2>
      <div className="governance-grid">
        <div className="governance-item">
          <div className="governance-label">Owner</div>
          <div className="governance-value">Facilities / Energy Manager</div>
        </div>
        <div className="governance-item">
          <div className="governance-label">Review Cadence</div>
          <div className="governance-value">90-day post-install validation</div>
        </div>
        <div className="governance-item">
          <div className="governance-label">If Underperforming</div>
          <div className="governance-value">Handled contractually</div>
        </div>
        <div className="governance-item">
          <div className="governance-label">Accountability</div>
          <div className="governance-value">Facilities: execution • Finance: verification</div>
        </div>
      </div>

      <div className="governance-note">
        Facilities is not accountable for savings realization; verification is financial and contractual.
      </div>

      <PageFooter pageNumber={7} />
    </div>
  );
}

// ============================================================================
// PAGE 8: RISK & SCALE ANALYSIS (NEW)
// ============================================================================

function Page8RiskScale({ roiResults, cfoMetrics }) {
  const summary = roiResults?.portfolio?.summary || {};

  // Scale calculations
  const totalEquipment = summary.count || 1;
  const unitSavings = (summary.total_annual_savings || 0) / totalEquipment;
  const unitCost = (summary.total_treatment_cost || 0) / totalEquipment;

  // Break-even threshold
  const capex = summary.total_treatment_cost || 0;
  const totalAnnualValue = summary.total_annual_value || summary.total_annual_savings || 0;
  const breakEvenThresholdPct = capex > 0 && totalAnnualValue > 0
    ? Math.round((capex / (totalAnnualValue * 5)) * 100)
    : 20;

  const scaleRows = [
    { scale: 'Current', units: totalEquipment, isCurrent: true },
    { scale: '100 units', units: 100 },
    { scale: '1,000 units', units: 1000 },
    { scale: '10,000 units', units: 10000 },
    { scale: '100,000 units', units: 100000 },
  ];

  const risks = [
    { risk: 'Savings variance from projection', likelihood: 'Low', impact: 'Low', mitigation: 'ASHRAE documents 2-3%/year efficiency loss. 113 PE-certified studies confirm treatment recovers this loss.' },
    { risk: 'Equipment replacement during treatment', likelihood: 'Low', impact: 'None', mitigation: 'Product guarantee provides treatment for replacement system at no additional cost.' },
    { risk: 'M&V measurement variability', likelihood: 'Low', impact: 'Low', mitigation: 'Chemical process — outcome is physics-based, not variable.' },
  ];

  return (
    <div className="print-page">
      <PageHeader title="Risk & Scale Analysis" />

      <h1 className="page-title">Risk & Scale Analysis</h1>
      <p className="page-subtitle">Downside protection and fleet expansion projections</p>

      {/* Downside Protection */}
      <div className="downside-callout">
        <span className="callout-icon">✓</span>
        <div className="callout-content">
          <div className="callout-title">Downside Protection</div>
          <div className="callout-text">
            Investment remains NPV-positive even if only <strong>{breakEvenThresholdPct}%</strong> of documented savings materialize.
          </div>
        </div>
      </div>

      {/* Risk Register */}
      <h2 className="section-title">Risk Register</h2>
      <table className="print-table risk-table">
        <thead>
          <tr>
            <th style={{ width: '25%' }}>Risk</th>
            <th style={{ width: '12%' }}>Likelihood</th>
            <th style={{ width: '12%' }}>Impact</th>
            <th style={{ width: '51%' }}>Mitigation</th>
          </tr>
        </thead>
        <tbody>
          {risks.map((row, idx) => (
            <tr key={idx}>
              <td>{row.risk}</td>
              <td><span className={`risk-badge ${row.likelihood.toLowerCase()}`}>{row.likelihood}</span></td>
              <td><span className={`risk-badge ${row.impact.toLowerCase()}`}>{row.impact}</span></td>
              <td className="mitigation-cell">{row.mitigation}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Universal Applicability */}
      <h2 className="section-title">Universal Applicability</h2>
      <p className="section-intro">Unlike VFDs or lighting retrofits, PermaFrost NMR treatment applies to all operational HVAC equipment:</p>
      <div className="applicability-grid">
        <div className="applicability-item">
          <span className="applicability-check">✓ Treat</span>
          <div className="applicability-label">New Equipment</div>
          <div className="applicability-note">Prevents degradation before it starts</div>
        </div>
        <div className="applicability-item">
          <span className="applicability-check">✓ Treat</span>
          <div className="applicability-label">Aged Equipment</div>
          <div className="applicability-note">Recovers lost capacity (20-25%)</div>
        </div>
        <div className="applicability-item">
          <span className="applicability-check">✓ Treat</span>
          <div className="applicability-label">Low Runtime</div>
          <div className="applicability-note">Economics scale proportionally</div>
        </div>
        <div className="applicability-item">
          <span className="applicability-check">✓ Treat</span>
          <div className="applicability-label">Replacement &lt;12mo</div>
          <div className="applicability-note">Guarantee covers replacement</div>
        </div>
      </div>
      <p className="exclusion-note"><strong>Only exclusion:</strong> Equipment scheduled for decommissioning within 6 months.</p>

      {/* Scale Projections */}
      <h2 className="section-title">Fleet Scale Projections</h2>
      <p className="section-intro">Per-unit economics validated for repeatable deployment across your installed base.</p>
      <table className="print-table scale-table">
        <thead>
          <tr>
            <th>Fleet Scale</th>
            <th>Annual Savings</th>
            <th>Treatment Cost</th>
            <th>5-Year Value</th>
          </tr>
        </thead>
        <tbody>
          {scaleRows.map((row) => (
            <tr key={row.scale} className={row.isCurrent ? 'current-row' : ''}>
              <td>
                {row.scale}
                {row.isCurrent && <span className="current-badge">This analysis</span>}
              </td>
              <td>{formatCurrency(unitSavings * row.units)}/yr</td>
              <td>{formatCurrency(unitCost * row.units)}</td>
              <td className="value-cell">{formatCurrency(unitSavings * row.units * 5)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Deployment Roadmap */}
      <h2 className="section-title">Deployment Roadmap</h2>
      <p className="section-intro">Start small, scale fast as results prove out:</p>
      <div className="roadmap-grid">
        <div className="roadmap-phase phase1">
          <div className="phase-title">Phase 1: Proof of Concept</div>
          <div className="phase-scope">5% or greater of portfolio</div>
          <div className="phase-gate">Gate: Verified savings confirm projections</div>
        </div>
        <div className="roadmap-phase phase2">
          <div className="phase-title">Phase 2: Rapid Scale</div>
          <div className="phase-scope">Remaining portfolio</div>
          <div className="phase-gate">Gate: Continue rollout as results prove out</div>
        </div>
      </div>

      <PageFooter pageNumber={8} />
    </div>
  );
}

// ============================================================================
// PAGE 9: EQUIPMENT ANALYSIS (was Page 7)
// ============================================================================

function Page9EquipmentAnalysis({ roiResults }) {
  const summary = roiResults?.portfolio?.summary || {};
  const byType = roiResults?.byType || {};
  const individual = roiResults?.individual || [];

  // Equipment types and age ranges for heatmap
  const EQUIPMENT_TYPES = ['Chiller', 'RTU', 'CRAC', 'Split'];
  const AGE_RANGES = [
    { label: '0-5 yr', min: 0, max: 5 },
    { label: '5-10 yr', min: 5, max: 10 },
    { label: '10-15 yr', min: 10, max: 15 },
    { label: '15+ yr', min: 15, max: 100 },
  ];

  // Build payback heatmap data
  const heatmapData = useMemo(() => {
    const grid = {};
    EQUIPMENT_TYPES.forEach(type => {
      grid[type] = {};
      AGE_RANGES.forEach(range => {
        grid[type][range.label] = { totalPayback: 0, count: 0 };
      });
    });

    individual.forEach(item => {
      const type = item.equipment_type;
      const age = item.age || 0;
      if (!EQUIPMENT_TYPES.includes(type)) return;

      const range = AGE_RANGES.find(r => age >= r.min && age < r.max);
      if (!range) return;

      grid[type][range.label].totalPayback += item.payback_months || 0;
      grid[type][range.label].count += 1;
    });

    return grid;
  }, [individual]);

  // Get cell color based on payback
  const getCellClass = (months) => {
    if (months === null) return '';
    if (months <= 12) return 'heatmap-fast';
    if (months <= 24) return 'heatmap-mid';
    return 'heatmap-slow';
  };

  // Equipment comparison data
  const activeTypes = Object.entries(byType).filter(([_, data]) => data.count > 0);

  // Equipment ROI Comparison Chart data
  const equipmentChartData = useMemo(() => {
    const types = activeTypes.map(([type]) => type);
    const savings = activeTypes.map(([_, data]) => data.total_annual_savings || 0);

    return {
      labels: types,
      datasets: [{
        label: 'Annual Savings',
        data: savings,
        backgroundColor: '#166534',
        borderRadius: 4,
        barThickness: 32,
      }]
    };
  }, [activeTypes]);

  const equipmentChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => `$${ctx.raw.toLocaleString()}`
        }
      },
      datalabels: { display: false }
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { font: { size: 10 } }
      },
      y: {
        grid: { color: '#E5E5E5' },
        ticks: {
          font: { size: 9 },
          callback: (val) => `$${(val / 1000).toFixed(0)}K`
        }
      }
    }
  };

  return (
    <div className="print-page">
      <PageHeader title="Equipment Analysis" />

      <h1 className="print-title">Equipment Analysis</h1>
      <p className="print-subtitle">Payback by type and age, ROI comparison</p>

      {/* Portfolio Summary Stats */}
      <h2 className="section-title">Portfolio Summary</h2>
      <div className="metrics-row">
        <MetricCard label="Total Equipment" value={summary.total_equipment || 0} subtext="units" />
        <MetricCard label="Total Tonnage" value={(summary.total_tonnage || 0).toLocaleString()} subtext="tons" />
        <MetricCard label="Avg Payback" value={`${formatMonths(summary.weighted_average_payback_months)} mo`} variant="positive" />
        <MetricCard label="5-Year ROI" value={`${(summary.five_year_roi || 0).toFixed(0)}%`} variant="accent" />
      </div>

      {/* Equipment ROI Comparison Chart */}
      {activeTypes.length > 0 && (
        <>
          <h2 className="section-title">Annual Savings by Equipment Type</h2>
          <div className="chart-container" style={{ height: '140px' }}>
            <Bar data={equipmentChartData} options={equipmentChartOptions} />
          </div>
          <p className="chart-caption">
            Larger capacity units typically yield higher absolute savings.
          </p>
        </>
      )}

      {/* Payback Heatmap */}
      <h2 className="section-title">Payback by Equipment Age (months)</h2>
      <table className="print-table heatmap-table">
        <thead>
          <tr>
            <th>Type</th>
            {AGE_RANGES.map(r => <th key={r.label} className="text-center">{r.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {EQUIPMENT_TYPES.map(type => {
            const hasData = AGE_RANGES.some(r => heatmapData[type]?.[r.label]?.count > 0);
            if (!hasData) return null;
            return (
              <tr key={type}>
                <td>{type}</td>
                {AGE_RANGES.map(range => {
                  const cell = heatmapData[type]?.[range.label];
                  const avgPayback = cell?.count > 0 ? cell.totalPayback / cell.count : null;
                  return (
                    <td
                      key={range.label}
                      className={`text-center font-mono ${getCellClass(avgPayback)}`}
                    >
                      {avgPayback !== null ? avgPayback.toFixed(1) : '—'}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className="chart-caption">
        Green (&lt;12 mo): Fast payback | Yellow (12-24 mo): Medium | Orange (&gt;24 mo): Slower
      </p>

      {/* Equipment Type Comparison */}
      <h2 className="section-title">Year 1 Cash Position by Type</h2>
      <table className="print-table">
        <thead>
          <tr>
            <th>Equipment Type</th>
            <th className="text-center">Units</th>
            <th className="text-right">Annual Savings</th>
            <th className="text-right">Treatment Cost</th>
            <th className="text-right">Y1 Net (Upfront)</th>
            <th className="text-right">Y1 Net (3-Year)</th>
          </tr>
        </thead>
        <tbody>
          {activeTypes.map(([type, data]) => {
            const upfrontY1 = (data.total_annual_savings || 0) - (data.total_treatment_cost || 0);
            const threeYearY1 = (data.total_annual_savings || 0) - ((data.total_treatment_cost || 0) / 3);
            return (
              <tr key={type}>
                <td>{type}</td>
                <td className="text-center font-mono">{data.count}</td>
                <td className="text-right font-mono">{formatCurrency(data.total_annual_savings)}</td>
                <td className="text-right font-mono">{formatCurrency(data.total_treatment_cost)}</td>
                <td className={`text-right font-mono ${upfrontY1 >= 0 ? 'positive' : 'negative'}`}>
                  {upfrontY1 >= 0 ? '+' : ''}{formatCurrency(upfrontY1)}
                </td>
                <td className="text-right font-mono positive">
                  +{formatCurrency(threeYearY1)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <div className="info-box" style={{ marginTop: '16px' }}>
        <div className="info-box-text">
          <strong>Analysis:</strong> Older equipment (10-15+ years) typically shows faster payback due to
          greater accumulated degradation. The 3-year payment option is cash-positive for all equipment
          types from Day 1.
        </div>
      </div>

      <PageFooter pageNumber={9} />
    </div>
  );
}

// ============================================================================
// PAGE 10: CAPITAL & ACCOUNTING (was Page 8)
// ============================================================================

function Page10Capital({ roiResults, cfoAnalysis }) {
  const summary = roiResults?.portfolio?.summary || {};
  const capitalPreservation = cfoAnalysis?.capitalPreservation || {};
  const accounting = cfoAnalysis?.accounting || {};

  return (
    <div className="print-page">
      <PageHeader title="Capital & Accounting" />

      <h1 className="print-title">Capital & Accounting Treatment</h1>
      <p className="print-subtitle">Working capital preservation and accounting comparison</p>

      {/* Capital Preservation */}
      <h2 className="section-title">Capital Preservation Analysis</h2>
      <p className="text-muted mb-3" style={{ fontSize: '10px' }}>
        Working capital retained in your business vs paying upfront
      </p>
      <div className="metrics-row metrics-row-3">
        <MetricCard
          label="Year 1 Capital Retained"
          value={formatCurrency(capitalPreservation.year1Retained)}
          subtext="Available for other uses"
          variant="positive"
        />
        <MetricCard
          label="Year 2 Capital Retained"
          value={formatCurrency(capitalPreservation.year2Retained)}
          subtext="Available for other uses"
          variant="positive"
        />
        <MetricCard
          label="Avg Capital Available"
          value={formatCurrency(capitalPreservation.averageCapitalAvailable)}
          subtext="Over 3 years"
        />
      </div>

      {/* Accounting Treatment Comparison */}
      <h2 className="section-title">Accounting Treatment Comparison</h2>
      <div className="comparison-grid">
        <div className="comparison-column">
          <div className="comparison-header">Upfront (CapEx)</div>
          <div className="metric-inline">
            <span className="metric-inline-label">Classification</span>
            <span className="metric-inline-value">{accounting.upfront?.type || 'CapEx'}</span>
          </div>
          <div className="metric-inline">
            <span className="metric-inline-label">Treatment</span>
            <span className="metric-inline-value">{accounting.upfront?.treatment || 'Capitalized asset'}</span>
          </div>
          <div className="metric-inline">
            <span className="metric-inline-label">Depreciation</span>
            <span className="metric-inline-value">{accounting.upfront?.depreciation || 'Over useful life'}</span>
          </div>
          <div className="metric-inline">
            <span className="metric-inline-label">Tax Impact</span>
            <span className="metric-inline-value">{accounting.upfront?.taxImpact || 'Spread over depreciation'}</span>
          </div>
          <div className="metric-inline">
            <span className="metric-inline-label">Approval</span>
            <span className="metric-inline-value">{accounting.upfront?.budgetApproval || 'CapEx approval required'}</span>
          </div>
        </div>

        <div className="comparison-column highlight">
          <div className="comparison-header">3-Year (OpEx)</div>
          <div className="metric-inline">
            <span className="metric-inline-label">Classification</span>
            <span className="metric-inline-value">{accounting.threeYear?.type || 'OpEx'}</span>
          </div>
          <div className="metric-inline">
            <span className="metric-inline-label">Treatment</span>
            <span className="metric-inline-value">{accounting.threeYear?.treatment || 'Operating expense'}</span>
          </div>
          <div className="metric-inline">
            <span className="metric-inline-label">Depreciation</span>
            <span className="metric-inline-value">{accounting.threeYear?.depreciation || 'N/A - expensed'}</span>
          </div>
          <div className="metric-inline">
            <span className="metric-inline-label">Tax Impact</span>
            <span className="metric-inline-value">{accounting.threeYear?.taxImpact || 'Full deduction in year'}</span>
          </div>
          <div className="metric-inline">
            <span className="metric-inline-label">Approval</span>
            <span className="metric-inline-value">{accounting.threeYear?.budgetApproval || 'Simpler OpEx budget'}</span>
          </div>
        </div>
      </div>
      <p className="credit-disclaimer">3-year deferred payment available to qualified buyers, subject to credit approval.</p>

      {/* Equipment Summary */}
      <h2 className="section-title">Equipment Summary</h2>
      <div className="equipment-summary-bar">
        <div className="equipment-summary-item">
          <div className="equipment-summary-value">{summary.total_equipment || 0}</div>
          <div className="equipment-summary-label">Units</div>
        </div>
        <div className="equipment-summary-item">
          <div className="equipment-summary-value">{(summary.total_tonnage || 0).toLocaleString()}</div>
          <div className="equipment-summary-label">Tons</div>
        </div>
        <div className="equipment-summary-item">
          <div className="equipment-summary-value">{formatMonths(summary.weighted_average_payback_months)} mo</div>
          <div className="equipment-summary-label">Payback</div>
        </div>
        <div className="equipment-summary-item">
          <div className="equipment-summary-value">{(summary.five_year_roi || 0).toFixed(0)}%</div>
          <div className="equipment-summary-label">5-Yr ROI</div>
        </div>
      </div>

      <EquipmentTable byType={roiResults?.byType} />

      <div className="methodology-note">
        <strong>Disclaimer:</strong> This assessment estimate is based on the PermaFrost NMR corpus containing
        174 case studies with 254 Professional Engineer-certified field measurements across 38 countries.
        Individual results reflect equipment-specific conditions. Consult your accountant for specific
        CapEx vs OpEx treatment in your jurisdiction.
        <br /><br />
        <strong>Contact:</strong> sales@powertronglobal.com | powertronglobal.com
      </div>

      <PageFooter pageNumber={10} />
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function EASPrintView() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Load data from sessionStorage
    try {
      const storedData = sessionStorage.getItem('easPrintData');
      if (storedData) {
        setData(JSON.parse(storedData));
      } else {
        setError('No data found. Please generate the report from the EAS Calculator.');
      }
    } catch (err) {
      console.error('Error loading print data:', err);
      setError('Error loading report data.');
    }
  }, []);

  const handlePrint = () => {
    window.print();
  };

  const handleClose = () => {
    window.close();
  };

  if (error) {
    return (
      <div className="eas-print-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <div style={{ textAlign: 'center', padding: '40px', background: 'white', borderRadius: '8px', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
          <h2 style={{ color: '#991B1B', marginBottom: '16px' }}>Report Error</h2>
          <p style={{ color: '#4A5568', marginBottom: '24px' }}>{error}</p>
          <button
            onClick={() => window.location.href = '/eas-calculator'}
            style={{
              padding: '12px 24px',
              background: '#1B4332',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: '600',
            }}
          >
            Go to EAS Calculator
          </button>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="eas-print-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <div style={{ textAlign: 'center', color: '#4A5568' }}>
          <p>Loading report...</p>
        </div>
      </div>
    );
  }

  const { roiResults, cfoMetrics, paymentMetrics, companyConfig, cfoAnalysis } = data;

  return (
    <div className="eas-print-container">
      {/* Print Controls (hidden when printing) */}
      <div className="print-controls">
        <h1>EAS Assessment Report Preview</h1>
        <div className="print-controls-buttons">
          <button className="secondary" onClick={handleClose}>Close</button>
          <button className="primary" onClick={handlePrint}>Print / Save as PDF</button>
        </div>
      </div>

      {/* Print Preview - 10 Pages */}
      <div className="print-preview">
        <Page1Summary roiResults={roiResults} cfoMetrics={cfoMetrics} paymentMetrics={paymentMetrics} />
        <Page2Upfront roiResults={roiResults} cfoMetrics={cfoMetrics} paymentMetrics={paymentMetrics} />
        <Page3EnergyESG roiResults={roiResults} cfoMetrics={cfoMetrics} />
        <Page4Projections roiResults={roiResults} paymentMetrics={paymentMetrics} />
        <Page5ValueCharts roiResults={roiResults} paymentMetrics={paymentMetrics} />
        <Page6Deferred roiResults={roiResults} paymentMetrics={paymentMetrics} cfoAnalysis={cfoAnalysis} />
        <Page7CFORecommendation roiResults={roiResults} cfoMetrics={cfoMetrics} companyConfig={companyConfig} />
        <Page8RiskScale roiResults={roiResults} cfoMetrics={cfoMetrics} />
        <Page9EquipmentAnalysis roiResults={roiResults} />
        <Page10Capital roiResults={roiResults} cfoAnalysis={cfoAnalysis} />
      </div>
    </div>
  );
}
